import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PlayCircle, Video, Zap, Layers, Sparkles } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { trackServiceActivate } from '@/lib/analytics-events';
import { PRODUCT_GROUP } from '@/lib/lead-product-group';
import { useSelfServeActivation } from '../activation-mode';
import { ServiceContactSalesDialog } from '../components/ServiceContactSalesDialog';
import {
  STREAMING_PRODUCTS,
  type StreamingProviderName,
} from './streaming-activate';
import { OnboardingCss } from '../waap/onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from '../waap/onboarding-styles';

interface StreamingActivateLandingProps {
  /** Animal codename of the provider to activate ('penguin' or 'lens'). */
  provider: StreamingProviderName;
  onBack?: () => void;
}

/**
 * Streaming catalog activation landing — the in-catalog first-run screen when
 * the org has no Penguin (Live Media Service) or Lens (Cloud Encoding) service.
 * Mirrors WaapActivateLanding: one click provisions the upstream tenant and
 * drops the customer into the product console.
 */
export function StreamingActivateLanding({ provider, onBack }: StreamingActivateLandingProps) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const { data: providers = [], isLoading: providersLoading } = useProviders();
  const selfServeActivation = useSelfServeActivation();

  const product = STREAMING_PRODUCTS[provider];

  // Resolve the provider record by animal name (case-insensitive), active only.
  const providerRecord = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === provider && p.status === 'active',
      ),
    [providers, provider],
  );

  const activate = useActivateService();
  const email = user?.email ?? '';
  const unavailable = !providersLoading && !providerRecord;
  const canActivate = !!providerRecord && !!email && !activate.isPending;
  // Self-serve provisioning is gated (contact-sales for direct customers, on
  // while staff impersonate); when off the CTA opens the contact-sales dialog.
  const [contactOpen, setContactOpen] = useState(false);

  function handleActivate() {
    if (!providerRecord || !canActivate) return;
    activate.mutate(
      { serviceName: product.serviceName, providerId: providerRecord.id, email },
      {
        onSuccess: (service) => {
          trackServiceActivate(provider, 'streaming');
          show({ tone: 'success', title: `${product.displayName} activated` });
          navigate(product.consoleRoute(service.id));
        },
        onError: (err) =>
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || `Could not activate ${product.displayName}. Please try again.`,
          }),
      },
    );
  }

  const perks = PERKS[provider];

  return (
    <Container size="xl">
      <PageHeader
        icon={<PlayCircle size={22} />}
        title={product.displayName}
        description={product.description}
        actions={
          onBack ? (
            <Button variant="ghost" startIcon={<ArrowLeft size={15} />} onClick={onBack}>
              Back to services
            </Button>
          ) : undefined
        }
      />

      {providersLoading ? (
        <Skeleton style={{ height: 200 }} />
      ) : unavailable ? (
        <Alert tone="warning" title={`${product.displayName} is not available yet`}>
          {product.displayName} is not available for self-serve activation on your account yet.
          Please contact our team to enable it.
        </Alert>
      ) : (
        <div className="vn-ob-hero" style={heroWrap}>
          <OnboardingCss />

          <span className="vn-ob-halo" style={heroIcon} aria-hidden>
            <PlayCircle size={30} />
          </span>

          <p style={heroEyebrow}>{product.displayName}</p>
          <h2 style={heroTitle}>Activate {product.displayName} for your organization</h2>
          <p style={heroLede}>{product.activateLede}</p>

          <Button
            size="lg"
            loading={selfServeActivation && activate.isPending}
            startIcon={<Sparkles size={16} />}
            onClick={selfServeActivation ? handleActivate : () => setContactOpen(true)}
            disabled={selfServeActivation ? !canActivate : false}
            style={{ marginTop: 'var(--vn-space-2)' }}
          >
            {selfServeActivation && activate.isPending
              ? 'Activating…'
              : `Activate ${product.displayName}`}
          </Button>

          <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
            {perks.map((p) => (
              <div key={p.title} style={stepRow}>
                <span style={markerAccent}>{p.icon}</span>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={stepTitle}>{p.title}</p>
                  <p style={stepDesc}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ServiceContactSalesDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        serviceLabel={product.displayName}
        productGroup={PRODUCT_GROUP.media}
      />
    </Container>
  );
}

// Per-provider feature highlights shown in the activation hero.
const PERKS: Record<StreamingProviderName, { icon: React.ReactNode; title: string; desc: string }[]> = {
  penguin: [
    { icon: <Video size={16} />, title: 'Live transcoding', desc: 'Real-time encode to HLS/DASH with multiple bitrate ladders and low-latency delivery.' },
    { icon: <Layers size={16} />, title: 'DRM protection', desc: 'Protect streams with Widevine and FairPlay DRM out of the box.' },
    { icon: <Zap size={16} />, title: 'Multi-CDN delivery', desc: 'Distribute live streams across multiple CDN backends for global reach.' },
  ],
  lens: [
    { icon: <Video size={16} />, title: 'Cloud encoding', desc: 'Encode video to any format and resolution with hardware-accelerated pipelines.' },
    { icon: <Layers size={16} />, title: 'Adaptive bitrate', desc: 'Package output as HLS/DASH with per-device bitrate ladder optimization.' },
    { icon: <Zap size={16} />, title: 'Watch folders', desc: 'Auto-ingest files from S3-compatible storage and trigger encode jobs automatically.' },
  ],
};

export default StreamingActivateLanding;
